import { getCountryByCode } from "../../../../utils/Helper";
import { toSentenceCase } from "../../../../global_functions/global";
import {
  liveSectionRows,
  mergeSectionDrafts,
  sectionRecordOf,
  sectionRowKey,
  sectionStatus,
} from "./sectionDrafts";

/* Related Entities and Business Relation — the rules they share.
 *
 * TRANSCRIBED from Details/RelatedEntities.jsx, Details/BusinessRelation.jsx
 * and their shared Details/AddRelatedForm.jsx.
 */

/* Transcribed: the stored value is snake_case and the card renders it as a
   sentence — "branch_(of_a_foreign_entity)" reads "Branch (of a foreign
   entity)". The same helper the live cards use. */
export const relationLabel = (value) =>
  toSentenceCase(String(value || "").replace(/_/g, " "));

/* Transcribed fallback chain, in order: the joined company's registered name,
   then a name on the row itself, then the `companyName` a freshly created row
   carries. All three occur in live data. */
export const relationCompanyName = (record) =>
  record?.companyID?.registered_company_name ||
  record?.registered_company_name ||
  record?.companyName ||
  "";

export const relationRowValues = (record = {}) => ({
  relation_type: relationLabel(record?.relation_type),
  company: relationCompanyName(record),
  state: record?.state || "",
  country: getCountryByCode(record?.country) || record?.country || "",
});

/* The rows in the order this spec's design shows them, with the company row
   carrying that section's own label. */
export const relationRows = (spec) =>
  (spec?.rowOrder || []).map((key) => ({
    key,
    label:
      key === "company"
        ? spec.companyRowLabel
        : key === "relation_type"
        ? "Relation type"
        : key === "state"
        ? "State"
        : "Country",
  }));

/* Transcribed: "raised" and "pending" join "Requested" as awaiting review. */
export const relationStatus = sectionStatus;

/* The merge and its two helpers are the flow-wide ones — six live sections
   performed the same twenty lines. See sectionDrafts.js. */
export const liveRelations = liveSectionRows;
export const relationOf = sectionRecordOf;
export const mergeRelationDrafts = mergeSectionDrafts;
export const relationKey = sectionRowKey;

/* Transcribed from AddRelatedForm's newErrors, messages included. */
export const validateRelation = ({ company, relationType, state, country }) => {
  const errors = {};
  if (String(company ?? "").trim() === "") errors.company = "Please specify a business";
  if (String(relationType ?? "").trim() === "") errors.relationType = "Please specify type";
  if (String(country ?? "").trim() === "") errors.country = "Please select country";
  if (String(state ?? "").trim() === "") errors.state = "Please specify state";
  return errors;
};

/* Transcribed: a searched business that exists only in WorldBox has no TREVEX
   id, so a company record is created first and the relation points at it. */
export const needsCompanyRecord = (chosen) =>
  Boolean(chosen && !chosen.trevex_id && !chosen.companyID && (chosen.win || chosen.wb_win));

/* The four fields every write carries, plus this spec's own company-name key
   and its request_type where it has one. */
export const relationPayload = ({ companyID, relationType, state, country, companyName }, spec) => ({
  relation_type: relationType,
  country,
  state,
  ...(companyName !== undefined ? { [spec.companyNameKey]: companyName } : {}),
  companyID,
});

/* Which of the four writes a submit performs — the same chain as every other
   section on this flow. */
export const relationSavePath = ({ verifiedCompany, editing }, spec) => {
  const verified =
    verifiedCompany !== null &&
    verifiedCompany !== undefined &&
    typeof verifiedCompany === "object" &&
    Object.keys(verifiedCompany).length !== 0;

  if (verified) {
    return editing?.[spec.draftIdKey] ? "updateDraft" : "createDraft";
  }
  return editing ? "update" : "create";
};
